import React, { useState, useEffect, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
  import {
    useModuleSubmitCodeMutation,
    useCompileCodeLazyQuery,
    useGetQuestionQuery,
    useGetStudentByEmailIdQuery,
    useGetCampusQuery,
    useModuleAutoSaveMutation,
    useGetAutoSubmitLazyQuery,
  } from "../../../graphql/generated.tsx";
import Compiler from "../../Practice/Components/Compiler";
import NetworkIndicator from "../../Practice/Components/NetworkIndicator.jsx";
import PracticeLayout from "../../Practice/Layout";
import BackButton from "../../Practice/Components/BackButton";
import CompilerQuestionCard from "../../Practice/Components/CompilerQuestionCard";
import CustomInput from "../../Practice/Components/CustomInput";
import RunModel from "../../Practice/Components/RunModel";
import SumbitModel from "../../Practice/Components/SumbitModel";
import ErrorModel from "../../Practice/Components/ErrorModel";

function ModuleCompilerScreen() {
  const location = useLocation();
  // Get completed state from navigation
  const isCompleted = location.state?.isCompleted || false;
  // Module AutoSave mutation
  const [moduleAutoSave] = useModuleAutoSaveMutation();
  const [getAutoSubmit, { data: autoSubmitData }] = useGetAutoSubmitLazyQuery();
  const { questionId, clusterId, courseId } = useParams();
  const { languages, studentId, module } = location.state || {};
  const [code, setCode] = useState("");
  const [language, setLanguage] = useState("C");
  const [output, setOutput] = useState("");
  const [error, setError] = useState("");
  const [input, setInput] = useState("");
  const [question, setQuestion] = useState({});
  const [isChecked, setIsChecked] = useState(false);
  const errorRef = useRef(null);
  const [testResults, setTestResults] = useState([]);
  const [run, setRun] = useState(false);
  const [submit, setSubmit] = useState(false);
  const tableRef = useRef(null);
  const [isFullScreen, setFullScreen] = useState(false);
  const [initialCode, setInitialCode] = useState("");
  const [isCodeChanged, setIsCodeChanged] = useState(false);
  const [codeApplied, setCodeApplied] = useState(false);
  // Removed previousSubmissionCode state, not needed

  const filterSampleResults = (results) => {
    const testCases = question?.test_cases || [];
    if (!results || testCases.length === 0 || results.length !== testCases.length) {
      return results || [];
    }

    const sampleIndexes = new Set(
      testCases
        .map((test, index) => (test.visibility === "SAMPLE" ? index : null))
        .filter((index) => index !== null)
    );

    return results.filter((_, index) => sampleIndexes.has(index));
  };

  const email = JSON.parse(localStorage.getItem("user"))?.email;
  const { data: studentData } = useGetStudentByEmailIdQuery({
    variables: { email },
    skip: !email,
  });
  const student = studentData?.getStudentByEmailId;

  const { data: campusData } = useGetCampusQuery({
    variables: { id: student?.campusId },
    skip: !student,
  });
  const campusDetails = campusData?.campus;

  // Autosave every 2 minutes (module version, only latest)
  useEffect(() => {
    const moduleId = module?.id || courseId;
    if (!student || !moduleId || !questionId || !location.state?.day_id || !location.state?.clusterId || !campusDetails?.name) {
      return;
    }
    const interval = setInterval(() => {
      handleSaveCode();
    }, 120000); // 2 minutes
    return () => clearInterval(interval);
  }, [student, courseId, module, questionId, location.state?.day_id, location.state?.clusterId, campusDetails?.name, code, language]);

  // Save function for both manual save and autosave (module version)
  const handleSaveCode = async () => {
    const moduleId = module?.id || courseId;
    if (!student || !moduleId || !questionId || !location.state?.day_id || !location.state?.clusterId || !campusDetails?.name) {
      return;
    }
    try {
      await moduleAutoSave({
        variables: {
          moduleSubmitInput: {
            student_id: String(student?.id),
            module_id: moduleId,
            day_id: location.state?.day_id,
            question_id: questionId,
            campus_name: campusDetails?.name,
            cluster_id: location.state?.clusterId,
            code: code,
            language: language,
            isCompleted: false,
            score: -1,
          },
          code: code,
          language: language,
        },
      });
      setInitialCode(code);
      setIsCodeChanged(false);
     
    } catch (err) {
      // Error suppressed (previously logged with console.error)
    }
  };
  
  const { data, loading } = useGetQuestionQuery({
    variables: { id: questionId },
  });

  const [
    compileCode,
    { data: compileData, loading: compileLoading, error: compileerror },
  ] = useCompileCodeLazyQuery();

  const [modulesubmitCode, { data: submitData, loading: submitLoading }] =
  useModuleSubmitCodeMutation();

  // Fetch autosaved code using getAutoSubmit when component mounts
  useEffect(() => {
    const moduleId = module?.id || courseId;
    if (student?.id && moduleId && questionId && campusDetails?.name && location.state?.day_id && location.state?.clusterId) {
      getAutoSubmit({
        variables: {
          getModuleSubmit: {
            student_id: String(student?.id),
            module_id: moduleId,
            day_id: location.state?.day_id,
            question_id: questionId,
            cluster_id: location.state?.clusterId,
            campus_name: campusDetails?.name,
          },
        },
      });
    }
  }, [student, courseId, module, questionId, campusDetails, getAutoSubmit, location.state?.day_id, location.state?.clusterId]);

  // Handle autosave data when it arrives and apply to editor only if language matches
  useEffect(() => {
    try {
      if (autoSubmitData?.getAutoSubmit?.autosave) {
        const autosave = autoSubmitData.getAutoSubmit.autosave;
        // Only apply autosave if its language matches the current editor language
        if (
          autosave.language &&
          autosave.language.toLowerCase() === language.toLowerCase() &&
          autosave.code
        ) {
          setCode(autosave.code);
          setInitialCode(autosave.code);
        }
      }
    } catch (err) {
      // Error suppressed (previously logged with console.error)
    }
  }, [autoSubmitData, language]);

  useEffect(() => {
    // Set the initial language from the languages prop when it's available.
    if (languages && languages.length > 0) {
      setLanguage(languages[0]);
    }
  }, [languages]);

  useEffect(() => {
    // Update the question and code when data or language changes, but only if no matching autosave is present
    if (data) {
      setQuestion(data.getQuestion);
      const autosave = autoSubmitData?.getAutoSubmit?.autosave;
      // Only set code if there is no autosave for this language
      if (!(
        autosave &&
        autosave.language &&
        autosave.language.toLowerCase() === language.toLowerCase() &&
        autosave.code
      )) {
        const predefined = data.getQuestion?.predefinedCode?.[language?.toLowerCase()];
        const fallbackCode =
          language?.toLowerCase() === "python"
            ? "# Type your code here"
            : "// Type your code here";
        setCode(predefined || fallbackCode);
        setInitialCode(predefined || fallbackCode);
      }
    }
  }, [data, language, autoSubmitData]);

  useEffect(() => {
    if (isFullScreen) {
      document.documentElement.requestFullscreen();
    } else {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      }
    }
  }, [isFullScreen]);

  useEffect(() => {
    if (tableRef.current) {
      tableRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [testResults]);

  useEffect(() => {
    if (error) {
      errorRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [error]);

  useEffect(() => {
    if (
      compileData &&
      compileData?.compileCode?.compilationOutput?.stderr === ""
    ) {
      setTestResults(filterSampleResults(compileData.compileCode.Results));
    } else {
      setTestResults([]);
    }
  }, [compileData]);

  useEffect(() => {
    if (submitData?.modulesubmitCode) {
      setOutput(submitData?.modulesubmitCode);
    }
  }, [submitData]);

  const handleCodeChange = (newValue) => {
    setCode(newValue);
  };

  useEffect(() => {
    setIsCodeChanged(code !== initialCode);
  }, [code, initialCode]);

  const handleCheckboxChange = () => {
    setIsChecked(!isChecked);
    setRun(false);
    setInput("");
  };

  const handleSubmit = async () => {
    // Use module.id from location state instead of courseId from URL params
    const moduleId = module?.id || courseId;
    
    setIsChecked(false);
    await modulesubmitCode({
      variables: {
        compilerCodeInput: {
          code,
          testCases: question?.test_cases?.map((test) => ({
            id: test.id,
            input: test.input,
            output: test.output,
            visibility: test.visibility,
            weightage: parseFloat(test.weightage),
          })),
          language,
          questionid: question.id,
        },
        moduleSubmitInput: {
          cluster_id: location.state?.clusterId,
          student_id: String(student?.id),
          question_id: questionId,
          module_id: moduleId,
          day_id: location.state?.day_id,
          campus_name: campusDetails?.name || "dummy-campus",
          code: code,
          language: language,
          isCompleted: false,
          score: -1,
        },
      },
    });
    setSubmit(true);
    setRun(false);
    setInput("");
  };

  const handleRun = async () => {
    try {
      setSubmit(false);
      setError("");
      let data;
      if (!isChecked) {
        const response = await compileCode({
          variables: {
            compileCodeInput: {
              code,
              testCases: question?.test_cases?.map((test) => ({
                id: test.id,
                input: test.input,
                output: test.output,
                visibility: test.visibility,
                weightage: parseFloat(test.weightage),
              })),
              language,
              questionid: question.id,
            },
          },
        });
        setRun(true);
        data = response.data;
      } else {
        const response = await compileCode({
          variables: {
            compileCodeInput: {
              code,
              testCases: [
                {
                  id: "0",
                  input: input,
                  output: " ",
                  visibility: "SAMPLE",
                  weightage: 0,
                },
              ],
              language,
              questionid: question?.id,
            },
          },
        });
        data = response.data;
      }
      if (data && data?.compileCode?.compilationOutput?.stderr === "") {
        setRun(true);
        setTestResults(filterSampleResults(data.compileCode.Results));
      } else {
        setTestResults([]);
      }
    } catch (err) {
      setTestResults([]);
      if (err?.graphQLErrors && err?.graphQLErrors.length > 0) {
        const errorMessage = err?.graphQLErrors[0]?.message;
        setError(errorMessage);
      } else {
        setError(
          "There could some logic error in your code or network connectivity issue. Kindly try again later"
        );
      }
    }
  };

  const setToDefault = () => {
    const predefined =
      question?.predefinedCode?.[language?.toLowerCase()];
    setCode(predefined || "//Type your code here");
  };

  return (
    <PracticeLayout isFullScreen={isFullScreen}>
      <NetworkIndicator />
      <BackButton setFullScreen={setFullScreen} isFullScreen={isFullScreen} />
      <div className={!isFullScreen ? "" : "flex"}>
        <div className={!isFullScreen ? "mb-5" : "w-1/2 pr-4"}>
          <CompilerQuestionCard
            question={question}
            loading={loading}
            hideSubmission={true}
            isCompleted={isCompleted}
          />
        </div>
        <div id="compiler-section" className={!isFullScreen ? "" : "w-1/2 pl-4"}>
          <Compiler
            student_id={student?.id ? String(student.id) : ""}
            setCodeApplied={setCodeApplied}
            language={language}
            code={code}
            setToDefault={setToDefault}
            setLanguage={setLanguage}
            handleCodeChange={handleCodeChange}
            errorRef={errorRef}
            handleSubmit={handleSubmit}
            handleRun={handleRun}
            CourseData={{ languages }}
            compileLoading={compileLoading}
            submitLoading={submitLoading}
            isChecked={isChecked}
            isCodeChanged={isCodeChanged}
            disabledReset={code === initialCode}
            isModule={true}
            moduleAutoSaveInput={{
              moduleSubmitInput: {
                student_id: String(student?.id),
                module_id: module?.id || courseId,
                day_id: location.state?.day_id,
                question_id: questionId,
                campus_name: campusDetails?.name,
                cluster_id: location.state?.clusterId,
                code: code,
                language: language,
                isCompleted: false,
                score: -1,
              },
              code: code,
              language: language,
            }}
            onCustomSave={handleSaveCode}
          />
          <CustomInput
            isChecked={isChecked}
            handleCheckboxChange={handleCheckboxChange}
            setInput={setInput}
            input={input}
          />

          {!compileerror && run && testResults && testResults.length > 0 && (
            <RunModel
              tableRef={tableRef}
              isChecked={isChecked}
              testResults={testResults}
              loading={compileLoading}
            />
          )}

          {output && submit && (
            <SumbitModel output={output} question={question} />
          )}

          {compileerror && !submit && (
            <ErrorModel error={compileerror.message ? String(compileerror.message) : String(compileerror)} language={language} />
          )}

          {(!testResults || testResults.length === 0) && !submit && run && !compileLoading && !compileerror && (
            <ErrorModel
              error={compileData?.compileCode?.compilationOutput?.stderr || 'No output or error returned.'}
              language={language}
              runtimeError={true}
            />
          )}
        </div>
      </div>
    </PracticeLayout>
  );
}

export default ModuleCompilerScreen;
